"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { loremParagraphs } from "@/lib/tool-kit/random-tools/lorem";

/* ------------------------------------------------------------------ */
/*  Regex constants                                                   */
/* ------------------------------------------------------------------ */

const RE_SENTENCES = /[^.!?]+[.!?]+/g;
const RE_SPLIT_WORDS = /\s+/;
const RE_TRAILING_PUNCT = /[,;:]$/;

/* ------------------------------------------------------------------ */
/*  Standard Lorem Ipsum corpus                                       */
/* ------------------------------------------------------------------ */

export const LOREM_PARAGRAPHS = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
  "Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris. Integer in mauris eu nibh euismod gravida. Duis ac tellus et risus vulputate vehicula. Donec lobortis risus a elit. Etiam tempor. Ut ullamcorper, ligula ut dictum pharetra, nisi nunc fringilla magna, in commodo elit erat nec turpis. Ut pharetra auctor nulla.",
  "Praesent dapibus, neque id cursus faucibus, tortor neque egestas augue, eu vulputate magna eros eu erat. Aliquam erat volutpat. Nam dui mi, tincidunt quis, accumsan porttitor, facilisis luctus, metus. Phasellus ultrices nulla quis nibh. Quisque a lectus. Donec consectetuer ligula vulputate sem tristique cursus. Nam nulla quam, gravida non, commodo a, sodales sit amet, nisi.",
  "Pellentesque fermentum dolor. Aliquam quam lectus, facilisis auctor, ultrices ut, elementum vulputate, nunc. Sed lacus. Donec lectus. Etiam vel nisi. Donec mauris. Maecenas auctor tellus id lectus. Etiam lacinia, felis nec blandit scelerisque, erat nulla commodo arcu, eget luctus nisi neque vitae metus. Morbi leo urna, gravida at, sagittis auctor, vulputate quis, lacus.",
  "Fusce vel dui. Sed in libero ut nibh placerat accumsan. Proin faucibus arcu quis ante. In consectetuer turpis ut velit. Nulla sit amet est. Praesent metus tellus, elementum eu, semper a, adipiscing nec, purus. Cras risus ipsum, faucibus ut, ullamcorper id, varius ac, leo. Suspendisse feugiat. Suspendisse enim turpis, dictum sed, iaculis a, condimentum nec, nisi.",
  "Praesent nec nisl a purus blandit viverra. Praesent ac massa at ligula laoreet iaculis. Nulla neque dolor, sagittis eget, iaculis quis, molestie non, velit. Mauris turpis nunc, blandit et, volutpat molestie, porta ut, ligula. Fusce quam. Cras id dui. Aenean ut eros et nisl sagittis vestibulum. Nullam nulla eros, ultricies sit amet, nonummy id, imperdiet feugiat, pede.",
  "Sed lectus. Donec mollis hendrerit risus. Phasellus nec sem in justo pellentesque facilisis. Etiam imperdiet imperdiet orci. Nunc nec neque. Phasellus leo dolor, tempus non, auctor et, hendrerit quis, nisi. Cras id dui. Aenean ut eros et nisl sagittis vestibulum. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia.",
  "Vivamus elementum semper nisi. Aenean vulputate eleifend tellus. Aenean leo ligula, porttitor eu, consequat vitae, eleifend ac, enim. Aliquam lorem ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius laoreet. Quisque rutrum. Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper ultricies nisi. Nam eget dui.",
];

const LOREM_SENTENCES: string[] = [];
for (const p of LOREM_PARAGRAPHS) {
  const sentences = p.match(RE_SENTENCES);
  if (sentences) {
    for (const s of sentences) {
      LOREM_SENTENCES.push(s.trim());
    }
  }
}

const LOREM_WORDS: string[] = [];
for (const p of LOREM_PARAGRAPHS) {
  for (const w of p.split(RE_SPLIT_WORDS)) {
    LOREM_WORDS.push(w);
  }
}

/* ------------------------------------------------------------------ */
/*  Generator functions                                               */
/* ------------------------------------------------------------------ */

function generateParagraphs(count: number, startClassic: boolean): string {
  return loremParagraphs(LOREM_PARAGRAPHS, count, startClassic).join("\n\n");
}

function generateSentences(count: number, startClassic: boolean): string {
  const result: string[] = [];
  const startIdx = startClassic ? 0 : 1;
  for (let i = 0; i < count; i++) {
    result.push(LOREM_SENTENCES[(startIdx + i) % LOREM_SENTENCES.length]);
  }
  return result.join(" ");
}

function generateWords(count: number, startClassic: boolean): string {
  const result: string[] = [];
  const startIdx = startClassic ? 0 : 5;
  for (let i = 0; i < count; i++) {
    result.push(LOREM_WORDS[(startIdx + i) % LOREM_WORDS.length]);
  }
  // Ensure it ends with a period
  const lastWord = result.at(-1);
  if (lastWord && !lastWord.endsWith(".")) {
    result[result.length - 1] = `${lastWord.replace(RE_TRAILING_PUNCT, "")}.`;
  }
  return result.join(" ");
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

type GenerateMode = "paragraphs" | "sentences" | "words";

/** English strings. A translated content file overrides these through `ui`. */
export const LOREM_IPSUM_GENERATOR_STRINGS = {
  generateBy: "Generate by",
  modeLabels: {
    paragraphs: "Paragraphs",
    sentences: "Sentences",
    words: "Words",
  },
  modeNouns: {
    paragraphs: "paragraphs",
    sentences: "sentences",
    words: "words",
  },
  numberOf: "Number of {noun} ({min}-{max})",
  startClassicLabel: "Start with “Lorem ipsum dolor sit amet...”",
  generate: "Generate",
  generatedText: "Generated Text",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
} as const;

export default function LoremIpsumGeneratorWidget() {
  const [mode, setMode] = useState<GenerateMode>("paragraphs");
  const [count, setCount] = useState(3);
  const [startClassic, setStartClassic] = useState(true);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(LOREM_IPSUM_GENERATOR_STRINGS);

  const limits: Record<GenerateMode, { min: number; max: number }> = {
    paragraphs: { min: 1, max: 20 },
    sentences: { min: 1, max: 100 },
    words: { min: 1, max: 5000 },
  };

  function handleGenerate() {
    const clamped = Math.max(
      limits[mode].min,
      Math.min(limits[mode].max, count)
    );
    let result = "";
    switch (mode) {
      case "paragraphs":
        result = generateParagraphs(clamped, startClassic);
        break;
      case "sentences":
        result = generateSentences(clamped, startClassic);
        break;
      case "words":
        result = generateWords(clamped, startClassic);
        break;
      default:
        break;
    }
    setOutput(result);
    setCopied(false);
  }

  async function handleCopy() {
    if (output) {
      if (!(await writeToClipboard(output))) {
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleModeChange(newMode: GenerateMode) {
    setMode(newMode);
    // Set sensible default count for each mode
    if (newMode === "paragraphs") {
      setCount(3);
    } else if (newMode === "sentences") {
      setCount(10);
    } else {
      setCount(100);
    }
  }

  return (
    <>
      {/* Controls */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        {/* Mode selector */}
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.generateBy}
          </p>
          <div className="flex flex-wrap gap-2">
            {(["paragraphs", "sentences", "words"] as const).map((m) => (
              <button
                className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                  mode === m
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:border-foreground/20 hover:bg-accent"
                }`}
                key={m}
                onClick={() => handleModeChange(m)}
                type="button"
              >
                {strings.modeLabels[m]}
              </button>
            ))}
          </div>
        </div>

        {/* Count */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="li-count"
          >
            {t(strings.numberOf, {
              noun: strings.modeNouns[mode],
              min: limits[mode].min,
              max: limits[mode].max,
            })}
          </label>
          <input
            className="w-32 rounded-lg border border-border bg-background p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="li-count"
            max={limits[mode].max}
            min={limits[mode].min}
            onChange={(e) => setCount(Number(e.target.value))}
            type="number"
            value={count}
          />
        </div>

        {/* Classic start toggle */}
        <label className="flex cursor-pointer items-center gap-3">
          <input
            checked={startClassic}
            className="h-4 w-4 rounded border-border accent-primary"
            onChange={(e) => setStartClassic(e.target.checked)}
            type="checkbox"
          />
          <span className="text-foreground text-sm">
            {strings.startClassicLabel}
          </span>
        </label>

        {/* Generate button */}
        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generate}
        </button>
      </div>

      {/* Output */}
      {output && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="font-medium text-foreground text-sm">
              {strings.generatedText}
            </p>
            <button
              className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/30 p-4 text-foreground text-sm leading-relaxed">
            {output}
          </div>
        </div>
      )}
    </>
  );
}
